import {
  Component,
  NgZone,
  OnDestroy,
  OnInit
} from '@angular/core';

import {
  CommonModule
} from '@angular/common';

import {
  Subject,
  takeUntil
} from 'rxjs';

import {
  loadMercadoPago
} from '@mercadopago/sdk-js';

import {
  environment
} from '../../../environments/environment';

import {
  RestaurantContextService
} from '../../core/restaurant-context.service';

import {
  RestaurantSubscription,
  SubscriptionDetailsResponse,
  SubscriptionPayment,
  SubscriptionPaymentStatus,
  SubscriptionService,
  SubscriptionStatus
} from '../../core/subscription.service';

@Component({
  selector: 'app-subscription',
  standalone: true,

  imports: [
    CommonModule
  ],

  templateUrl:
    './subscription.component.html',

  styleUrl:
    './subscription.component.css'
})
export class SubscriptionComponent
  implements OnInit, OnDestroy {

  private readonly destroy$ =
    new Subject<void>();

  private mercadoPago: any = null;
  private cardForm: any = null;

  restaurantId:
    number | null = null;

  subscription:
    RestaurantSubscription | null = null;

  hasAccess = false;
  trialDaysRemaining = 0;

  loading = false;

  paymentFormLoading = false;
  paymentSubmitting = false;
  paymentFormReady = false;

  errorMessage = '';
  successMessage = '';

  showPaymentArea = false;

  constructor(
    private readonly subscriptionService:
      SubscriptionService,

    private readonly restaurantContext:
      RestaurantContextService,

    private readonly ngZone:
      NgZone
  ) {}

  ngOnInit(): void {
    const restaurant =
      this.restaurantContext
        .getRestaurant();

    if (!restaurant?.id) {
      this.errorMessage =
        'Nenhum restaurante foi selecionado.';

      return;
    }

    this.restaurantId =
      Number(restaurant.id);

    this.loadSubscription();
  }

  ngOnDestroy(): void {
    this.destroyCardForm();

    this.destroy$.next();
    this.destroy$.complete();
  }

  loadSubscription(): void {
    if (!this.restaurantId) {
      return;
    }

    this.loading = true;
    this.clearMessages();

    this.subscriptionService
      .getSubscription(
        this.restaurantId
      )
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (
          response:
            SubscriptionDetailsResponse
        ) => {
          this.subscription =
            response.subscription;

          this.hasAccess =
            response.hasAccess;

          this.trialDaysRemaining =
            response.trialDaysRemaining;

          this.loading = false;
        },

        error: (error) => {
          this.loading = false;

          console.error(
            'Erro ao carregar assinatura:',
            error
          );

          this.errorMessage =
            error?.error?.message ||
            'Não foi possível carregar a assinatura.';
        }
      });
  }

  async openSubscriptionPayment():
    Promise<void> {

    this.clearMessages();

    if (!this.subscription) {
      this.errorMessage =
        'Assinatura não encontrada.';

      return;
    }

    if (
      !this.subscription.plan
        .mercadoPagoPlanId
    ) {
      this.errorMessage =
        'O plano ainda não está configurado para pagamento.';

      return;
    }

    if (
      !environment
        .mercadoPagoPublicKey
        ?.trim()
    ) {
      this.errorMessage =
        'A Public Key do Mercado Pago não foi configurada.';

      return;
    }

    this.showPaymentArea = true;
    this.paymentFormLoading = true;
    this.paymentFormReady = false;
    this.paymentSubmitting = false;

    /*
     * O formulário está dentro de um *ngIf.
     * É necessário aguardar o Angular criar
     * os elementos antes de iniciar o CardForm.
     */
    setTimeout(async () => {
      try {
        await this
          .initializeMercadoPagoForm();

        document
          .getElementById(
            'subscription-payment-area'
          )
          ?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
      } catch (error) {
        console.error(
          'Erro ao iniciar Mercado Pago:',
          error
        );

        this.ngZone.run(() => {
          this.paymentFormLoading =
            false;

          this.paymentFormReady =
            false;

          this.errorMessage =
            'Não foi possível carregar o formulário de pagamento.';
        });
      }
    }, 150);
  }

  closeSubscriptionPayment(): void {
    if (this.paymentSubmitting) {
      return;
    }

    this.showPaymentArea = false;
    this.paymentFormLoading = false;
    this.paymentFormReady = false;

    this.destroyCardForm();
  }

  private async initializeMercadoPagoForm():
    Promise<void> {

    this.destroyCardForm();

    await loadMercadoPago();

    const MercadoPagoClass =
      (window as any).MercadoPago;

    if (!MercadoPagoClass) {
      throw new Error(
        'SDK do Mercado Pago não foi carregado.'
      );
    }

    const publicKey =
      environment
        .mercadoPagoPublicKey
        .trim();

    this.mercadoPago =
      new MercadoPagoClass(
        publicKey,
        {
          locale: 'pt-BR'
        }
      );

    this.cardForm =
      this.mercadoPago.cardForm({
        amount:
          this.planPrice.toFixed(2),

        iframe: true,

        form: {
          id:
            'form-checkout',

          cardNumber: {
            id:
              'form-checkout__cardNumber',

            placeholder:
              'Número do cartão'
          },

          expirationDate: {
            id:
              'form-checkout__expirationDate',

            placeholder:
              'MM/AA'
          },

          securityCode: {
            id:
              'form-checkout__securityCode',

            placeholder:
              'CVV'
          },

          cardholderName: {
            id:
              'form-checkout__cardholderName',

            placeholder:
              'Nome impresso no cartão'
          },

          issuer: {
            id:
              'form-checkout__issuer',

            placeholder:
              'Banco emissor'
          },

          installments: {
            id:
              'form-checkout__installments',

            placeholder:
              'Parcelas'
          },

          identificationType: {
            id:
              'form-checkout__identificationType',

            placeholder:
              'Tipo de documento'
          },

          identificationNumber: {
            id:
              'form-checkout__identificationNumber',

            placeholder:
              'Número do documento'
          },

          cardholderEmail: {
            id:
              'form-checkout__cardholderEmail',

            placeholder:
              'E-mail do responsável'
          }
        },

        callbacks: {
          onFormMounted: (
            error: unknown
          ) => {
            this.ngZone.run(() => {
              this.paymentFormLoading =
                false;

              if (error) {
                console.error(
                  'Erro ao montar CardForm:',
                  error
                );

                this.paymentFormReady =
                  false;

                this.errorMessage =
                  'Não foi possível carregar os campos do cartão.';

                return;
              }

              this.paymentFormReady =
                true;
            });
          },

          onSubmit: (
            event: Event
          ) => {
            event.preventDefault();

            if (
              this.paymentSubmitting
            ) {
              return;
            }

            const formData =
              this.cardForm
                ?.getCardFormData();

            this.ngZone.run(() => {
              this.processCardForm(
                formData
              );
            });
          },

          onFetching: (
            resource: string
          ) => {
            console.log(
              'Mercado Pago consultando:',
              resource
            );

            return () => {
              /*
               * Executado quando a consulta
               * ao recurso é finalizada.
               */
            };
          }
        }
      });
  }

  private processCardForm(
    formData: any
  ): void {
    this.clearMessages();

    const cardTokenId =
      String(
        formData?.token || ''
      ).trim();

    const payerEmail =
      String(
        formData?.cardholderEmail ||
        ''
      )
        .trim()
        .toLowerCase();


const deviceId =
  String(
    (window as any)
      .MP_DEVICE_SESSION_ID ||
    ''
  ).trim();

console.log(
  'Mercado Pago Device ID:',
  deviceId
    ? 'capturado'
    : 'não encontrado'
);


    if (!cardTokenId) {
      this.errorMessage =
        'Não foi possível gerar o token do cartão. Confira os dados informados.';

      return;
    }

    if (!payerEmail) {
      this.errorMessage =
        'Informe o e-mail do responsável pelo pagamento.';

      return;
    }

    this.authorizeSubscription(
      cardTokenId,
      payerEmail,
      deviceId
    );
  }

  private authorizeSubscription(
    cardTokenId: string,
    payerEmail: string,
      deviceId: string
  ): void {
    if (
      !this.restaurantId ||
      !this.subscription
    ) {
      this.errorMessage =
        'Não foi possível identificar o restaurante ou a assinatura.';

      return;
    }

    this.paymentSubmitting = true;
    this.clearMessages();

    this.subscriptionService
      .authorizeSubscription(
        this.restaurantId,
        {
          cardTokenId,
          payerEmail
        }
      )
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response) => {
          this.paymentSubmitting =
            false;

          this.showPaymentArea =
            false;

          this.paymentFormReady =
            false;

          this.destroyCardForm();

          this.successMessage =
            response?.message ||
            'Assinatura criada com sucesso.';

          /*
           * Não chama clearMessages aqui,
           * para não apagar a mensagem de
           * sucesso imediatamente.
           */
          this.reloadSubscriptionAfterPayment();
        },

        error: (error) => {
          this.paymentSubmitting =
            false;

          console.error(
            'Erro ao contratar assinatura:',
            error
          );

          this.errorMessage =
            error?.error?.message ||
            'Não foi possível contratar a assinatura.';

          /*
           * O cardToken é de uso único.
           * Recria o formulário para gerar
           * um novo token em outra tentativa.
           */
          this.paymentFormLoading =
            true;

          this.paymentFormReady =
            false;

          setTimeout(() => {
            if (
              !this.showPaymentArea
            ) {
              return;
            }

            this.initializeMercadoPagoForm()
              .catch(
                initializeError => {
                  console.error(
                    'Erro ao reiniciar CardForm:',
                    initializeError
                  );

                  this.ngZone.run(() => {
                    this.paymentFormLoading =
                      false;

                    this.paymentFormReady =
                      false;
                  });
                }
              );
          }, 150);
        }
      });
  }

  private reloadSubscriptionAfterPayment():
    void {
    if (!this.restaurantId) {
      return;
    }

    this.loading = true;

    this.subscriptionService
      .getSubscription(
        this.restaurantId
      )
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (
          response:
            SubscriptionDetailsResponse
        ) => {
          this.subscription =
            response.subscription;

          this.hasAccess =
            response.hasAccess;

          this.trialDaysRemaining =
            response.trialDaysRemaining;

          this.loading = false;
        },

        error: (error) => {
          this.loading = false;

          console.error(
            'Erro ao atualizar assinatura:',
            error
          );

          this.errorMessage =
            error?.error?.message ||
            'A assinatura foi enviada, mas não foi possível atualizar os dados da tela.';
        }
      });
  }

  private destroyCardForm(): void {
    if (
      this.cardForm &&
      typeof this.cardForm.unmount ===
        'function'
    ) {
      try {
        this.cardForm.unmount();
      } catch (error) {
        console.warn(
          'Não foi possível desmontar o CardForm:',
          error
        );
      }
    }

    this.cardForm = null;
    this.mercadoPago = null;
  }

  updateCard(): void {
    this.clearMessages();

    this.successMessage =
      'A atualização de cartão será habilitada na próxima etapa da integração.';
  }

  cancelSubscription(): void {
    this.clearMessages();

    this.successMessage =
      'O cancelamento será conectado ao Mercado Pago na próxima etapa.';
  }

  get payments():
    SubscriptionPayment[] {
    return (
      this.subscription?.payments || []
    );
  }

  get hasPayments(): boolean {
    return this.payments.length > 0;
  }

  get statusLabel(): string {
    if (!this.subscription) {
      return 'Sem assinatura';
    }

    return this.getStatusLabel(
      this.subscription.status
    );
  }

  get statusDescription(): string {
    const status =
      this.subscription?.status;

    const descriptions:
      Partial<
        Record<
          SubscriptionStatus,
          string
        >
      > = {
        TRIAL:
          'Você está utilizando o período gratuito.',

        PENDING:
          'Estamos aguardando a confirmação do pagamento.',

        ACTIVE:
          'Seu plano está ativo e o acesso está liberado.',

        PAST_DUE:
          'Existe uma cobrança pendente. Atualize o pagamento para evitar bloqueio.',

        PAUSED:
          'Sua assinatura está temporariamente pausada.',

        CANCELED:
          'A assinatura foi cancelada e não será renovada.',

        EXPIRED:
          'Seu período de acesso terminou.'
      };

    return status
      ? descriptions[status] || ''
      : '';
  }

  get planPrice(): number {
    return this.toNumber(
      this.subscription?.plan?.price
    );
  }

  get trialProgressText(): string {
    if (
      this.subscription?.status !==
      'TRIAL'
    ) {
      return '';
    }

    if (
      this.trialDaysRemaining <= 0
    ) {
      return (
        'Seu período de teste terminou.'
      );
    }

    if (
      this.trialDaysRemaining === 1
    ) {
      return (
        'Resta 1 dia de teste grátis.'
      );
    }

    return (
      `Restam ${this.trialDaysRemaining} ` +
      'dias de teste grátis.'
    );
  }

  get trialProgressPercentage(): number {
    const totalDays =
      Number(
        this.subscription
          ?.plan?.trialDays || 0
      );

    if (
      totalDays <= 0 ||
      this.subscription?.status !==
        'TRIAL'
    ) {
      return 0;
    }

    const elapsedDays =
      totalDays -
      this.trialDaysRemaining;

    const percentage =
      (
        elapsedDays /
        totalDays
      ) * 100;

    return Math.min(
      100,
      Math.max(
        0,
        percentage
      )
    );
  }

  get canSubscribe(): boolean {
    return Boolean(
      this.subscription &&
      [
        'TRIAL',
        'PENDING',
        'PAST_DUE',
        'CANCELED',
        'EXPIRED'
      ].includes(
        this.subscription.status
      )
    );
  }

  get canManageActiveSubscription():
    boolean {
    return Boolean(
      this.subscription
        ?.mercadoPagoSubscriptionId &&
      [
        'ACTIVE',
        'PAST_DUE',
        'PAUSED'
      ].includes(
        this.subscription.status
      )
    );
  }

  get accessLabel(): string {
    return this.hasAccess
      ? 'Acesso liberado'
      : 'Acesso bloqueado';
  }

  get accessDescription(): string {
    return this.hasAccess
      ? 'O restaurante pode utilizar normalmente as funcionalidades do sistema.'
      : 'Regularize sua assinatura para recuperar o acesso completo.';
  }

  get nextBillingLabel(): string {
    if (
      this.subscription?.status ===
      'TRIAL'
    ) {
      return (
        'A cobrança só começará após a contratação do plano.'
      );
    }

    if (
      this.subscription
        ?.nextBillingAt
    ) {
      return this.formatDate(
        this.subscription.nextBillingAt
      );
    }

    return 'Ainda não definida';
  }

  getStatusLabel(
    status:
      SubscriptionStatus
  ): string {
    const labels:
      Record<
        SubscriptionStatus,
        string
      > = {
        TRIAL:
          'Período de teste',

        PENDING:
          'Aguardando confirmação',

        ACTIVE:
          'Assinatura ativa',

        PAST_DUE:
          'Pagamento pendente',

        PAUSED:
          'Assinatura pausada',

        CANCELED:
          'Assinatura cancelada',

        EXPIRED:
          'Período expirado'
      };

    return labels[status];
  }

  getPaymentStatusLabel(
    status:
      SubscriptionPaymentStatus
  ): string {
    const labels:
      Record<
        SubscriptionPaymentStatus,
        string
      > = {
        PENDING:
          'Pendente',

        APPROVED:
          'Aprovado',

        REJECTED:
          'Recusado',

        CANCELED:
          'Cancelado',

        REFUNDED:
          'Reembolsado',

        CHARGED_BACK:
          'Contestado'
      };

    return (
      labels[status] ||
      status
    );
  }

  getPaymentMethodLabel(
    method?: string | null
  ): string {
    if (!method) {
      return '-';
    }

    const labels:
      Record<string, string> = {
        credit_card:
          'Cartão de crédito',

        debit_card:
          'Cartão de débito',

        account_money:
          'Saldo Mercado Pago',

        pix:
          'Pix',

        ticket:
          'Boleto'
      };

    return (
      labels[method] ||
      method
    );
  }

  getPaymentDate(
    payment:
      SubscriptionPayment
  ): string {
    return this.formatDate(
      payment.approvedAt ||
      payment.paidAt ||
      payment.createdAt
    );
  }

  formatDate(
    value?: string | null
  ): string {
    if (!value) {
      return '-';
    }

    const date =
      new Date(value);

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return '-';
    }

    return new Intl.DateTimeFormat(
      'pt-BR',
      {
        dateStyle: 'medium'
      }
    ).format(date);
  }

  formatMoney(
    value:
      number |
      string |
      null |
      undefined
  ): string {
    return new Intl.NumberFormat(
      'pt-BR',
      {
        style: 'currency',
        currency: 'BRL'
      }
    ).format(
      this.toNumber(value)
    );
  }

  private toNumber(
    value:
      number |
      string |
      null |
      undefined
  ): number {
    const parsed =
      Number(value || 0);

    return Number.isFinite(parsed)
      ? parsed
      : 0;
  }

  private clearMessages(): void {
    this.errorMessage = '';
    this.successMessage = '';
  }
}